import * as React from 'react';

import QuoteSection from '@/components/sections/QuoteSection';
import RenderSimpleHtml from '@core/components/display/string/RenderSimpleHtml';
import SectionSpeakers from '@/components/pages/event/layout/components/SectionSpeakers';
import fetchEventSection from '@/components/pages/event/utils/fetchEventsSection';
import SectionPastAttendees from '@/components/pages/event/layout/components/SectionPastAttendees';
import SectionSponsors from '@/components/pages/event/layout/components/SectionSponsors';
import SectionTickets from '@/components/pages/event/layout/components/SectionTickets';
import SectionFollow from '@/components/pages/event/layout/components/home/SectionFollow';
import Countdown from '@/components/pages/event/layout/components/home/components/Countdown';
import SectionHeader from '@/components/display/headers/SectionHeader';
import ImageWithFallback from '@core/components/display/images/ImageWithFallback';
import slugify from '@core/utils/functions/slugify';
import ImageSlider from '@/components/pages/event/layout/components/home/components/ImageSlider';
import nextImageFromCustomFields from '@/components/pages/events/components/utils/nextImageFromCustomFields';
import type { TabLayoutProps } from '@/components/pages/event/types/TabLayout';
import type { Content, PhotosContent, Quote } from '@/types/EventsCustomFields';
import type { NextImage } from '@research-api/utils/api/ghost/ghost-interfaces';

type HomeLayoutProps = TabLayoutProps & {
    pricing: Content[];
};

const HomeLayout = (props: HomeLayoutProps) => {
    const { event, speakers, accentColor, pricing } = props;

    const accentColorStyle = { backgroundColor: accentColor };

    const highlightedSpeaker = speakers && speakers.length > 0 ? speakers[0]! : null;
    const pastAttendees = fetchEventSection(event.customFields?.content || [], 'participating_companies');
    const quotes = fetchEventSection(event.customFields?.content || [], 'quote-slider');
    const sponsorsAll = event.customFields?.content?.filter(ev => ev.acf_fc_layout === 'founding_sponsors') || [];
    const experience = fetchEventSection(event.customFields?.content || [], 'experience');
    const photos = fetchEventSection(event.customFields?.content || [], 'photo-slider') as PhotosContent;

    const countdownDate = event.customFields?.countdown_date
        ? new Date(new Date(event.customFields?.countdown_date).toLocaleString('en', { timeZone: 'America/New_York' }))
        : null;

    return (
        <>
            {countdownDate && countdownDate > new Date() && (
                <div style={{ color: accentColor }}>
                    <Countdown
                        text={event.customFields?.countdown_text}
                        date={countdownDate}
                        className="flex flex-col xl:flex-row xl:align-middle gap-3 lg:gap-5 bg-white p-5 md:p-10 xl:px-20 md:pr-32 xl:pr-32"
                    />
                </div>
            )}
            <QuoteSection
                title={event.customFields.home_info || ''}
                label=""
                accentColor={accentColor}
                className="bg-[#f7f7f7] pb-12 md:px-8 lg:p-12 xl:p-20 bg-white p-6 md:pl-12 md:pr-28"
            >
                <div className="md:pr-12 text-md lg:text-xl text-left text-[#110b29] prose prose-purple max-w-none prose-headings:font-display prose-headings:font-normal lg:prose-headings:scroll-mt-[8.5rem] prose-lead:text-purple-500 prose-a:font-base prose-a:border-none prose-a:hover:border-none prose-a:underline prose-pre:rounded-xl prose-pre:bg-purple-900 prose-pre:shadow-lg">
                    <RenderSimpleHtml html={event.content} />
                </div>
            </QuoteSection>
            {speakers && (
                <SectionSpeakers
                    className="px-2 pb-12 md:px-8 lg:p-12 xl:p-20 bg-white px-6 md:px-12 md:pr-32 lg:pr-32 xl:pr-32"
                    eventSlug={event.slug}
                    highlightedSpeaker={highlightedSpeaker}
                    speakers={speakers}
                    accentColor={accentColor}
                    displayCount={9}
                    showAllSpeakersLink
                />
            )}

            {experience && (
                <section className="mt-12 flex flex-col items-center justify-start gap-10 px-2 pb-12 md:px-8 lg:p-12 xl:p-20 bg-white px-6 md:px-12 md:pr-32 lg:pr-32 xl:pr-32">
                    <SectionHeader className="px-0 py-5" title="Experience" linkTitle="" linkUrl="" />
                    {experience.text_above && (
                        <div>
                            <RenderSimpleHtml html={experience.text_above} />
                        </div>
                    )}
                    <div className="flex flex-col items-center gap-10">
                        {experience.experience_item?.map((item: any) => (
                            <div
                                key={slugify(item.title)}
                                className="flex flex-col lg:flex-row text-center lg:text-left justify-start items-center gap-5 px-6 md:px-24 lg:px-0"
                            >
                                <div className="min-w-[250px] lg:min-w-[335px]">
                                    <ImageWithFallback
                                        width={335}
                                        height={185}
                                        src={item.image}
                                        alt={item.title}
                                        className="w-full h-full bg-gray-50 object-cover w-[335px] h-[185px]"
                                    />
                                </div>
                                <div className="flex flex-col relative gap-3">
                                    <p className="text-[32px] text-[#000a39]">{item.title}</p>
                                    <div className="text-lg text-[#000a39]">
                                        <RenderSimpleHtml html={item.description} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {experience.text_below && (
                        <div>
                            <RenderSimpleHtml html={experience.text_below} />
                        </div>
                    )}
                </section>
            )}

            {pricing.length > 0 &&
                pricing.map((price, idx) => (
                    <SectionTickets
                        className="p-5 md:p-10 xl:px-20 md:pr-32 xl:pr-32 pb-96"
                        key={`pricing-${idx}`}
                        eventSlug={event.slug}
                        pricing={price}
                        accentColor={accentColor}
                    />
                ))}

            {pastAttendees && (
                <SectionPastAttendees
                    className="p-5 md:p-10 xl:px-20 md:pr-32 xl:pr-32 pb-96"
                    eventSlug={event.slug}
                    pastAttendees={pastAttendees}
                    accentColor={accentColor}
                    limit={8}
                    showAllLink
                />
            )}
            {quotes && (
                <>
                    {quotes.quote_slider?.map((quote: Quote) => (
                        <section
                            key={quote.quote_name}
                            className="flex flex-col justify-start items-start flex-grow-0 flex-shrink-0 w-full gap-2.5 px-60 py-20"
                            style={accentColorStyle}
                        >
                            <div className="flex flex-col justify-start items-center self-stretch flex-grow-0 flex-shrink-0 relative gap-5">
                                {quote.quote && (
                                    <div className="self-stretch flex-grow-0 flex-shrink-0 w-[960px] text-[28px] text-center text-white">
                                        <RenderSimpleHtml html={quote.quote} />
                                    </div>
                                )}
                                <p className="flex-grow-0 flex-shrink-0 text-lg text-center text-white">
                                    — {quote.quote_name}
                                </p>
                            </div>
                        </section>
                    ))}
                </>
            )}
            {sponsorsAll.length > 0 &&
                sponsorsAll.map(
                    (sponsors, idx) =>
                        sponsors.sponsor_group && (
                            <SectionSponsors
                                className="p-5 md:p-10 xl:px-20 md:pr-32 xl:pr-32"
                                key={`sponsors-${idx}`}
                                eventSlug={event.slug}
                                sponsorGroup={sponsors.sponsor_group}
                                accentColor={accentColor}
                                showAllLink
                            />
                        )
                )}

            {photos && photos.photo_slider && photos.photo_slider.length > 0 && (
                <div className="w-full">
                    <ImageSlider
                        imageUrls={photos.photo_slider.map(photo => nextImageFromCustomFields(photo) as NextImage)}
                    />
                </div>
            )}

            <SectionFollow accentColor={accentColor}>
                <p>
                    For the latest news and updates about {event.title}, follow{' '}
                    <a
                        className="font-bold underline"
                        href={`https://twitter.com/@${event.customFields?.twitter_handle || 'Blockworks_'}`}
                        rel="nofollow"
                    >
                        @{event.customFields?.twitter_handle || 'Blockworks_'}
                    </a>
                    .
                </p>
            </SectionFollow>
        </>
    );
};

export default HomeLayout;
